import React from 'react';
import { Check } from 'lucide-react';

export const VISUAL_STYLES = [
  { id: 'none', label: 'No fixed style', blurb: 'Each scene prompt sets its own look', free: true },
  { id: 'photorealistic', label: 'Photorealistic', blurb: 'Real camera, real light' },
  { id: 'cinematic_film', label: 'Cinematic Film', blurb: '35mm, graded, anamorphic' },
  { id: 'animation_3d', label: 'Animation (3D)', blurb: 'Pixar-grade CGI' },
  { id: 'anime', label: 'Anime', blurb: 'Cel-shaded, Ghibli-esque' },
  { id: 'watercolor', label: 'Watercolor', blurb: 'Soft, painterly washes' },
  { id: 'noir', label: 'Noir / B&W', blurb: 'High-contrast shadows' },
  { id: 'comic_book', label: 'Comic Book', blurb: 'Ink lines, halftone' },
  { id: 'vintage_retro', label: 'Vintage / Retro', blurb: 'Faded Super 8 warmth' },
  { id: 'surreal', label: 'Surreal', blurb: 'Dreamlike, impossible' },
  { id: 'pixel_art', label: 'Pixel Art', blurb: 'Retro game sprites' },
];

export const PIXEL_BIT_DEPTHS = [
  { id: 'pixel_art_8bit', label: '8-bit', hint: 'NES era · chunky, 4–16 colours' },
  { id: 'pixel_art_16bit', label: '16-bit', hint: 'SNES era · detailed sprites, dithering' },
  { id: 'pixel_art_32bit', label: '32-bit', hint: 'PS1 / GBA era · hi-res pixels, rich palette' },
];

export const baseStyleId = (value) => (value || '').startsWith('pixel_art') ? 'pixel_art' : (value || 'photorealistic');

export const styleLabel = (value) => {
  const base = VISUAL_STYLES.find(s => s.id === baseStyleId(value));
  if (!base) return value || 'Photorealistic';
  const depth = PIXEL_BIT_DEPTHS.find(d => d.id === value);
  return depth ? `${base.label} · ${depth.label}` : base.label;
};

export const styleThumb = (value) => `/styles/${baseStyleId(value) === 'none' ? 'photorealistic' : baseStyleId(value)}.jpg`;

const FreeStyleTile = ({ active }) => (
  <div className={`w-full h-full grid grid-cols-3 ${active ? 'opacity-100' : 'opacity-75'}`} aria-hidden>
    {['photorealistic', 'anime', 'watercolor', 'noir', 'pixel_art', 'comic_book'].map(k => (
      <img key={k} src={`/styles/${k}.jpg`} alt="" className="w-full h-full object-cover" style={k === 'pixel_art' ? { imageRendering: 'pixelated' } : undefined} />
    ))}
  </div>
);

// Shared visual-style picker: thumbnail cards + bit-depth sub-options when Pixel Art is chosen.
export const StyleSelector = ({ value, onChange, columns = 'grid-cols-2 sm:grid-cols-3 md:grid-cols-5', testIdPrefix = 'style' }) => {
  const base = baseStyleId(value);
  const pick = (id) => onChange(id === 'pixel_art' ? (value?.startsWith('pixel_art') ? value : 'pixel_art_16bit') : id);
  return (
    <div className="space-y-3">
      <div className={`grid ${columns} gap-2`} data-testid={`${testIdPrefix}-selector`}>
        {VISUAL_STYLES.map(s => {
          const active = base === s.id;
          return (
            <button key={s.id} type="button" data-testid={`${testIdPrefix}-${s.id}`} onClick={() => pick(s.id)} aria-pressed={active}
              className={`group relative rounded-xl overflow-hidden border text-left transition-[border-color,transform,box-shadow] duration-200 hover:-translate-y-0.5 ${
                active ? 'border-[#A78BFA] shadow-[0_0_0_1px_rgba(167,139,250,0.5),0_8px_24px_-8px_rgba(167,139,250,0.5)]' : 'border-white/[0.08] hover:border-white/25'}`}>
              <div className="aspect-video w-full bg-[#121216]">
                {s.free ? <FreeStyleTile active={active} /> : (
                <img src={styleThumb(s.id)} alt={`${s.label} sample`} loading="lazy" draggable={false}
                  className={`w-full h-full object-cover transition-[transform,opacity] duration-300 group-hover:scale-105 ${active ? 'opacity-100' : 'opacity-80 group-hover:opacity-100'}`}
                  style={s.id === 'pixel_art' ? { imageRendering: 'pixelated' } : undefined} />
                )}
              </div>
              <div className={`px-2.5 py-2 ${active ? 'bg-[#A78BFA]/10' : 'bg-[#0d0d11]'}`}>
                <div className="flex items-center justify-between gap-1">
                  <span className={`text-xs font-semibold truncate ${active ? 'text-white' : 'text-[#C9C9CF]'}`}>{s.label}</span>
                  {active && <Check className="w-3.5 h-3.5 text-[#A78BFA] shrink-0" />}
                </div>
                <p className="text-[10px] text-[#8A8A93] truncate">{s.blurb}</p>
              </div>
            </button>
          );
        })}
      </div>
      {base === 'pixel_art' && (
        <div data-testid={`${testIdPrefix}-pixel-depth`} className="rounded-xl border border-[#A78BFA]/25 bg-[#A78BFA]/[0.06] p-3">
          <p className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[#A78BFA] mb-2">Sprite detail · bit depth</p>
          <div className="grid grid-cols-3 gap-2">
            {PIXEL_BIT_DEPTHS.map(d => {
              const active = value === d.id;
              return (
                <button key={d.id} type="button" data-testid={`${testIdPrefix}-${d.id}`} onClick={() => onChange(d.id)} aria-pressed={active}
                  className={`rounded-lg border px-3 py-2 text-left transition-colors ${active ? 'bg-[#A78BFA]/20 border-[#A78BFA]/60 text-white' : 'bg-white/[0.02] border-white/[0.08] text-[#C9C9CF] hover:border-white/25'}`}>
                  <span className="text-sm font-bold font-mono">{d.label}</span>
                  <p className="text-[10px] text-[#8A8A93] leading-snug mt-0.5">{d.hint}</p>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
